import { getAuth } from "firebase/auth";
import { getApiUrl } from "./config";

const API_BASE_URL = getApiUrl();

/**
 * List vendor bids for a ticket (auth required; landlord must own the ticket).
 * @param {string} ticketId
 * @returns {Promise<{ success: boolean, bids: object[], count: number }>}
 */
export async function getTicketBids(ticketId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  if (!token) {
    throw new Error("Sign in to view bids for this ticket.");
  }
  const res = await fetch(
    `${API_BASE_URL}/vendor-bids/tickets/${encodeURIComponent(ticketId)}/bids`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load bids (${res.status})`);
  }
  return res.json();
}

/**
 * Accept a vendor bid. Backend creates a work order in pending_approval; use approveWorkOrder to dispatch.
 * @param {string} bidId - Bid identifier
 * @param {{ ticketId?: string, workOrderId?: string, landlordId?: string }} [options]
 * @returns {Promise<{ success: boolean, workOrderId?: string }>}
 */
export async function acceptBid(bidId, options = {}) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${API_BASE_URL}/vendor-bids/${encodeURIComponent(bidId)}/accept`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        ticketId: options.ticketId ?? undefined,
        workOrderId: options.workOrderId ?? undefined,
        landlordId: options.landlordId ?? undefined,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || err.error || `Accept bid failed (${res.status})`
    );
  }
  return res.json();
}

/**
 * Reject a vendor bid.
 * @param {string} bidId - Bid identifier
 * @param {{ landlordId?: string }} [options]
 * @returns {Promise<{ success: boolean }>}
 */
export async function rejectBid(bidId, options = {}) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${API_BASE_URL}/vendor-bids/${encodeURIComponent(bidId)}/reject`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ landlordId: options.landlordId }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || err.error || `Reject bid failed (${res.status})`
    );
  }
  return res.json();
}

/**
 * Get work orders for a ticket (for showing Approve/Deny when status is pending_approval).
 * @param {string} ticketId - Repair ticket id
 * @returns {Promise<{ success: boolean, workOrders: Array<{ id: string, status: string, ... }> }>}
 */
export async function getWorkOrdersForTicket(ticketId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${API_BASE_URL}/vendor-bids/tickets/${encodeURIComponent(
      ticketId
    )}/work-orders`,
    {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load work orders (${res.status})`);
  }
  return res.json();
}

/**
 * Approve or deny a work order. When approved, contractor is notified and landlord CC'd.
 * @param {string} workOrderId - Work order id
 * @param {'approved'|'denied'} status
 * @param {{ landlordId?: string }} [options]
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function updateWorkOrderStatus(workOrderId, status, options = {}) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${API_BASE_URL}/vendor-bids/work-orders/${encodeURIComponent(
      workOrderId
    )}/status`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ status, landlordId: options.landlordId }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Update work order failed (${res.status})`);
  }
  return res.json();
}
